import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  type ReactElement,
  type Ref,
} from "react";
import { FilterBar } from "../ui/barra-filtros";
import { DataContent } from "./DataContent";
import { DataPagination, PaginationState } from "./DataPagination";
import { cn } from "@/lib/utils";
import { useDataTableLogic } from "@/hooks/data-table/use-data-table";
import { useColumnResize } from "@/hooks/data-table/use-column-resize";
import { useFullscreen } from "@/hooks/data-table/use-fullscreen";
import { usePermissions } from "@/hooks/use-permissions";
import { exportToCSV } from "@/utils/export-table";

export interface Column<T> {
  type?: "headless" | "data";
  key: string;
  title: string;
  /**
   * O valor **de exportação** — número cru, sem formatação, para o CSV somar na planilha.
   * Não é texto de tela: quem precisa disso é o `cellTitle`.
   */
  raw?: (item: T) => string;
  /**
   * O balão nativo da célula, para revelar o que truncou. Só faz sentido em coluna com `render`
   * próprio: sem ele, o que está na tela **é** o `raw`, e o balão sai de graça.
   *
   * Existe porque o `raw` já servia à exportação e estava sendo reusado como balão: numa coluna
   * de dinheiro isso mostrava `510.78` por cima de "R$ 510,78" — o balão contradizendo a tela.
   * String vazia é o jeito de dizer "esta coluna não quer balão".
   */
  cellTitle?: (item: T) => string;
  tooltip?: React.ReactNode;
  sortable?: boolean;
  width?: string;
  render?: (item: T, onRowClick?: (row: T) => void) => React.ReactNode;
  visible?: boolean;
}

export interface ColumnState<T> extends Column<T> {
  isVisible: boolean;
}

/** API imperativa opcional do DataTable, acessível via `ref`. */
export interface DataTableHandle<C> {
  /** Mescla `patch` no critério de filtro atual (dispara refetch + reset de página). */
  setFilter: (patch: Partial<C>) => void;
}

interface DataTableProps<T, F, C> {
  storageKey?: string;
  data: T[];
  columns: Column<T>[];
  className?: string;
  isLoading?: boolean;
  actionButton?: React.ReactNode;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  /** URL da linha (rota de detalhe). Habilita "abrir em nova aba" (botão direito/ctrl/meio-clique). */
  getRowHref?: (row: T) => string | undefined;
  rowClassName?: (row: T) => string;
  onFetchData?: (criteria: C) => Promise<void> | void;
  onFetchFilters?: () => Promise<F>;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  defaultSortColumn?: string;
  defaultSortOrder?: "ASC" | "DESC";
  searchPlaceholder?: string;
  emptyMessage?: React.ReactNode | string;
  /** Sinaliza falha na busca: troca o estado vazio por um estado de erro. */
  isError?: boolean;
  /** Mensagem usada no estado de erro (default: texto genérico). */
  errorMessage?: React.ReactNode | string;
  /** Tenta refazer a busca a partir do estado de erro (ex.: `invalidate` do hook). */
  onRetry?: () => void;
  renderSelectionColumn?: (item: T) => React.ReactNode;

  // Auto-refresh
  autoRefreshInterval?: number;

  // Export CSV: fornecer o nome base do arquivo para habilitar o botão de exportação
  exportFilename?: string;
  /**
   * Busca o dataset completo (todas as páginas) para exportar. Quando ausente, o
   * export usa só a página carregada e o botão deixa isso explícito ("Exportar página").
   */
  onExportAll?: () => Promise<T[]>;

  // Contador de resultados (override; por padrão usa pagination.total)
  totalCount?: number;
}

function DataTableInner<T, F, C>(
  {
    storageKey,
    data,
    columns,
    className,
    isLoading,
    actionButton,
    onFetchData,
    onFetchFilters,
    pagination,
    onPaginationChange,
    defaultSortColumn,
    defaultSortOrder,
    emptyMessage,
    isError,
    errorMessage,
    onRetry,
    onRowClick,
    onRowDoubleClick,
    getRowHref,
    rowClassName,
    searchPlaceholder = "Buscar...",
    renderSelectionColumn,
    autoRefreshInterval,
    exportFilename,
    onExportAll,
    totalCount,
  }: Readonly<DataTableProps<T, F, C>>,
  ref: Ref<DataTableHandle<C>>,
) {
  const { state, actions } = useDataTableLogic({
    storageKey,
    initialColumns: columns,
    defaultSortColumn,
    defaultSortOrder,
    onFetchData,
    onFetchFilters,
    pagination,
    autoRefreshInterval,
  });

  useImperativeHandle(
    ref,
    () => ({
      setFilter: (patch) =>
        actions.setFilterCriteria({
          ...((state.filterCriteria as unknown as C) ?? ({} as C)),
          ...patch,
        } as unknown as F),
    }),
    [actions, state.filterCriteria],
  );

  const { columnWidths, startResize } = useColumnResize({
    initialWidths: state.columnWidths,
    onWidthsChange: actions.setColumnWidths,
  });

  const { isFullscreen, toggleFullscreen } = useFullscreen();
  const { isAdmin } = usePermissions();

  const isTableLoading = isLoading !== undefined ? isLoading : state.isLoading;

  const visibleColumns = useMemo(
    () => state.columnStates.filter((col) => col.isVisible || col.key === "select"),
    [state.columnStates],
  );

  const handleSortChange = useCallback(
    (col: string, order: "ASC" | "DESC") => {
      actions.setSortColumn(col);
      actions.setSortOrder(order);
    },
    [actions],
  );

  const handleExport = useMemo(() => {
    if (!exportFilename || !isAdmin) return undefined;
    return async () => {
      const rows = onExportAll ? await onExportAll() : data;
      exportToCSV(rows, visibleColumns as ColumnState<T>[], exportFilename);
    };
  }, [exportFilename, isAdmin, data, visibleColumns, onExportAll]);

  // Sem fonte para todas as páginas: o export é parcial — o botão deixa isso explícito.
  const exportTitle = onExportAll ? "Exportar CSV" : "Exportar página (CSV)";

  const resolvedTotalCount = totalCount ?? pagination?.total;

  const hasActiveFilters = useMemo(() => {
    if (state.searchTerm.trim().length > 0) return true;
    const criteria = state.filterCriteria as Record<string, unknown> | null;
    if (!criteria) return false;
    return Object.values(criteria).some((v) =>
      Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "",
    );
  }, [state.searchTerm, state.filterCriteria]);

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        isFullscreen ? "fixed inset-0 z-[60] bg-background px-6 py-4" : "",
        className,
      )}
    >
      <FilterBar
        storageKey={storageKey}
        columns={state.columnStates}
        searchValue={state.searchTerm}
        searchPlaceholder={searchPlaceholder}
        onSearchChange={actions.setSearchTerm}
        onClearAll={actions.handleClearAll}
        filtrosCriteria={state.filterCriteria as unknown as C}
        setFiltrosCriteria={actions.setFilterCriteria as unknown as (c: C) => void}
        filters={state.availableFilters}
        onColumnsChange={(cols) => actions.setColumnStates(cols as ColumnState<T>[])}
        actionButton={actionButton}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        onExport={handleExport}
        exportTitle={exportTitle}
        totalCount={resolvedTotalCount}
      />

      <DataContent
        data={data}
        columns={visibleColumns}
        isLoading={isTableLoading}
        isFullscreen={isFullscreen}
        sortColumn={state.sortColumn}
        sortOrder={state.sortOrder}
        onSortChange={handleSortChange}
        emptyMessage={emptyMessage}
        isError={isError}
        errorMessage={errorMessage}
        onRetry={onRetry}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={actions.handleClearAll}
        onRowClick={onRowClick}
        onRowDoubleClick={onRowDoubleClick}
        getRowHref={getRowHref}
        rowClassName={rowClassName}
        renderSelectionColumn={renderSelectionColumn}
        columnWidths={columnWidths}
        onStartResize={startResize}
      />

      <DataPagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        disabled={isTableLoading}
      />
    </div>
  );
}

// forwardRef apaga os genéricos; o cast os reintroduz na assinatura pública.
// `ref` é opcional, então as chamadas existentes (sem ref) seguem inalteradas.
export const DataTable = forwardRef(DataTableInner) as <T, F, C>(
  props: DataTableProps<T, F, C> & { ref?: Ref<DataTableHandle<C>> },
) => ReactElement;
